import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { useSession } from './hooks/useSession';
import Layout from './components/Layout/Layout';
import Login from './pages/Login';
import Register from './pages/Register';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import Dashboard from './pages/Dashboard';
import Courses from './pages/Courses';
import CourseDetail from './pages/CourseDetail';
import LessonView from './pages/LessonView';
import AdminCourses from './pages/AdminCourses';
import AdminCompanies from './pages/AdminCompanies';
import AdminEmployees from './pages/AdminEmployees';
import AdminEvaluations from './pages/AdminEvaluations';
import AdminDomainRequests from './pages/AdminDomainRequests';
import AdminDomainRequestsGrouped from './pages/AdminDomainRequestsGrouped';
import AdminCompanyAnalytics from './pages/AdminCompanyAnalytics';
import CompanyDashboard from './pages/CompanyDashboard';
import CompanyEmployees from './pages/CompanyEmployees';
import CompanyEmployeesPendingCourses from './pages/CompanyEmployeesPendingCourses';
import CompanyDepartments from './pages/CompanyDepartments';
import CompanyOffices from './pages/CompanyOffices';
import CompanyCertificates from './pages/CompanyCertificates';
import BulkUploadEmployees from './pages/BulkUploadEmployees';
import BulkUploadDepartments from './pages/BulkUploadDepartments';
import BulkUploadOffices from './pages/BulkUploadOffices';
import DepartmentProgress from './pages/DepartmentProgress';
import OfficeProgress from './pages/OfficeProgress';
import AllResults from './pages/AllResults';
import AdminAllResults from './pages/AdminAllResults';
import Profile from './pages/Profile';
import Onboarding from './pages/Onboarding';
import VerifyEmail from './pages/VerifyEmail';
import SetPassword from './pages/SetPassword';

const PrivateRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, loading } = useAuth();
  useSession(); // Verificar sesión periódicamente
  
  if (loading) return <div>Cargando...</div>;
  if (!user) return <Navigate to="/login" replace />;
  
  // Si el usuario es empleado y no tiene sede o departamento asignado, redirigir a onboarding
  const canChangeOrgData = user.company?.settings?.allowEmployeeChangeCompanyOffice ?? true;
  if (user.role === 'employee' && canChangeOrgData && (!user.office || !user.department)) {
    // Solo redirigir si no estamos ya en la página de onboarding
    if (window.location.pathname !== '/onboarding') {
      return <Navigate to="/onboarding" replace />;
    }
  }
  
  return <>{children}</>;
};

const App: React.FC = () => {
  return (
    <AuthProvider>
      <Router>
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Register />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/verify-email" element={<VerifyEmail />} />
          <Route path="/set-password" element={<SetPassword />} />
          <Route
            path="/onboarding"
            element={
              <PrivateRoute>
                <Onboarding />
              </PrivateRoute>
            }
          />
          <Route
            path="/dashboard"
            element={
              <PrivateRoute>
                <Layout>
                  <Dashboard />
                </Layout>
              </PrivateRoute>
            }
          />
          <Route
            path="/courses"
            element={
              <PrivateRoute>
                <Layout>
                  <Courses />
                </Layout>
              </PrivateRoute>
            }
          />
          <Route
            path="/courses/:courseId"
            element={
              <PrivateRoute>
                <Layout>
                  <CourseDetail />
                </Layout>
              </PrivateRoute>
            }
          />
          <Route
            path="/courses/:courseId/lessons/:lessonId"
            element={
              <PrivateRoute>
                <Layout>
                  <LessonView />
                </Layout>
              </PrivateRoute>
            }
          />
          <Route
            path="/profile"
            element={
              <PrivateRoute>
                <Layout>
                  <Profile />
                </Layout>
              </PrivateRoute>
            }
          />
          <Route
            path="/admin/courses"
            element={
              <PrivateRoute>
                <Layout>
                  <AdminCourses />
                </Layout>
              </PrivateRoute>
            }
          />
          <Route
            path="/admin/companies"
            element={
              <PrivateRoute>
                <Layout>
                  <AdminCompanies />
                </Layout>
              </PrivateRoute>
            }
          />
          <Route
            path="/admin/employees"
            element={
              <PrivateRoute>
                <Layout>
                  <AdminEmployees />
                </Layout>
              </PrivateRoute>
            }
          />
          <Route
            path="/admin/domain-requests"
            element={
              <PrivateRoute>
                <Layout>
                  <AdminDomainRequests />
                </Layout>
              </PrivateRoute>
            }
          />
          <Route
            path="/admin/domain-requests-grouped"
            element={
              <PrivateRoute>
                <Layout>
                  <AdminDomainRequestsGrouped />
                </Layout>
              </PrivateRoute>
            }
          />
          <Route
            path="/admin/evaluations"
            element={
              <PrivateRoute>
                <Layout>
                  <AdminEvaluations />
                </Layout>
              </PrivateRoute>
            }
          />
          <Route
            path="/admin/analytics/companies"
            element={
              <PrivateRoute>
                <Layout>
                  <AdminCompanyAnalytics />
                </Layout>
              </PrivateRoute>
            }
          />
          <Route
            path="/admin/all-results"
            element={
              <PrivateRoute>
                <Layout>
                  <AdminAllResults />
                </Layout>
              </PrivateRoute>
            }
          />
          <Route
            path="/company/dashboard"
            element={
              <PrivateRoute>
                <Layout>
                  <CompanyDashboard />
                </Layout>
              </PrivateRoute>
            }
          />
          <Route
            path="/company/employees"
            element={
              <PrivateRoute>
                <Layout>
                  <CompanyEmployees />
                </Layout>
              </PrivateRoute>
            }
          />
          <Route
            path="/company/employees-pending-courses"
            element={
              <PrivateRoute>
                <Layout>
                  <CompanyEmployeesPendingCourses />
                </Layout>
              </PrivateRoute>
            }
          />
          <Route
            path="/company/departments"
            element={
              <PrivateRoute>
                <Layout>
                  <CompanyDepartments />
                </Layout>
              </PrivateRoute>
            }
          />
          <Route
            path="/company/offices"
            element={
              <PrivateRoute>
                <Layout>
                  <CompanyOffices />
                </Layout>
              </PrivateRoute>
            }
          />
          <Route
            path="/company/bulk-upload"
            element={
              <PrivateRoute>
                <Layout>
                  <BulkUploadEmployees />
                </Layout>
              </PrivateRoute>
            }
          />
          <Route
            path="/company/bulk-upload-departments"
            element={
              <PrivateRoute>
                <Layout>
                  <BulkUploadDepartments />
                </Layout>
              </PrivateRoute>
            }
          />
          <Route
            path="/company/bulk-upload-offices"
            element={
              <PrivateRoute>
                <Layout>
                  <BulkUploadOffices />
                </Layout>
              </PrivateRoute>
            }
          />
          <Route
            path="/company/certificates"
            element={
              <PrivateRoute>
                <Layout>
                  <CompanyCertificates />
                </Layout>
              </PrivateRoute>
            }
          />
          <Route
            path="/company/department-progress"
            element={
              <PrivateRoute>
                <Layout>
                  <DepartmentProgress />
                </Layout>
              </PrivateRoute>
            }
          />
          <Route
            path="/company/office-progress"
            element={
              <PrivateRoute>
                <Layout>
                  <OfficeProgress />
                </Layout>
              </PrivateRoute>
            }
          />
          <Route
            path="/company/all-results"
            element={
              <PrivateRoute>
                <Layout>
                  <AllResults />
                </Layout>
              </PrivateRoute>
            }
          />
          <Route
            path="/company/evaluations"
            element={
              <PrivateRoute>
                <Layout>
                  <AdminEvaluations />
                </Layout>
              </PrivateRoute>
            }
          />
          <Route path="*" element={<Navigate to="/dashboard" replace />} />
        </Routes>
      </Router>
    </AuthProvider>
  );
};

export default App;
